import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Edit2, Loader, Mail, Phone, Plus, RefreshCw, Search, ShieldCheck, Trash2, User, UserPlus } from "lucide-react";

const emptyForm = {
  name: "",
  phone: "",
  email: "",
  password: "",
};

const ROLE_LABELS = {
  "super-admin": "Society admin",
  "aqua-admin": "Aqua admin",
  admin: "Society user",
};

const getRoleLabel = (role) => ROLE_LABELS[role] || role || "-";
const INDIAN_PHONE_PATTERN = /^[6-9]\d{9}$/;
const PHONE_ERROR = "Phone number must be a valid 10-digit Indian mobile number";

const normalizePhoneInput = (value) => String(value || "").replace(/\D/g, "").slice(0, 10);

const Users = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const fetchUsers = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const data = await api.getAdminUsers();
      setUsers(Array.isArray(data) ? data : []);
      if (isRefresh) {
        toast.success("Users refreshed successfully");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;

    return users.filter((user) =>
      [user.name, user.email, user.phone, user.role, getRoleLabel(user.role)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }, [search, users]);

  const handleChange = (field, value) => {
    setFormData((current) => ({
      ...current,
      [field]: field === "phone" ? normalizePhoneInput(value) : value,
    }));
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setFormData(emptyForm);
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || "",
      phone: user.phone || "",
      email: user.email || "",
      password: "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const phone = formData.phone.trim();
    if (phone && !INDIAN_PHONE_PATTERN.test(phone)) {
      toast.error(PHONE_ERROR);
      return;
    }

    setCreating(true);

    try {
      if (editingUser) {
        const payload = {
          email: formData.email,
          name: formData.name,
          phone: phone || null,
        };
        if (formData.password) {
          payload.password = formData.password;
        }
        await api.updateAdminUser(editingUser.id, payload);
        toast.success("Admin user updated successfully");
      } else {
        await api.createAdminUser({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          phone: phone || null,
        });
        toast.success("Admin user created successfully");
      }
      closeDialog();
      await fetchUsers(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save admin user");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (user) => {
    const label = user.name || user.email || "this admin user";
    if (!window.confirm(`Delete ${label}? This will remove their assignments too.`)) {
      return;
    }

    setDeletingUserId(user.id);
    try {
      await api.deleteAdminUser(user.id);
      toast.success("Admin user deleted successfully");
      await fetchUsers(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete admin user");
    } finally {
      setDeletingUserId(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] flex-col items-center justify-center">
          <Loader className="h-10 w-10 animate-spin text-[hsl(199,89%,48%)]" />
          <p className="mt-4 text-slate-600">Loading users...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">Users</h1>
            <p className="mt-1 text-slate-600">
              Create and list admin users for AquaTank.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => fetchUsers(true)}
              disabled={refreshing}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={openCreateDialog}
              className="gap-2 bg-slate-900 text-white hover:bg-slate-700"
            >
              <Plus className="h-4 w-4" />
              Create User
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="flex items-center gap-4 pt-6">
              <div className="rounded-lg bg-cyan-100 p-3 text-cyan-700">
                <User className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Admin Users</p>
                <p className="text-3xl font-bold text-slate-900">{users.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="md:col-span-2">
            <CardContent className="pt-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search users by name, email, phone, or role"
                  className="pl-10"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Admin Users</CardTitle>
            <CardDescription>
              Only admin role accounts are created from this section.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead className="font-semibold text-slate-700">Name</TableHead>
                    <TableHead className="font-semibold text-slate-700">Email</TableHead>
                    <TableHead className="font-semibold text-slate-700">Phone</TableHead>
                    <TableHead className="font-semibold text-slate-700">Role</TableHead>
                    <TableHead className="font-semibold text-slate-700">Created</TableHead>
                    <TableHead className="w-24 text-center font-semibold text-slate-700">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="py-3 font-medium text-slate-900">
                          {user.name || user.username || "-"}
                        </TableCell>
                        <TableCell className="py-3 text-slate-700">{user.email}</TableCell>
                        <TableCell className="py-3 text-slate-700">{user.phone || "-"}</TableCell>
                        <TableCell className="py-3">
                          <Badge variant="secondary" className="gap-1">
                            <ShieldCheck className="h-3 w-3" />
                            {getRoleLabel(user.role)}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-3 text-slate-700">
                          {user.created_at
                            ? new Date(user.created_at).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell className="py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(user)}
                              className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                              title="Edit admin user"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(user)}
                              disabled={deletingUserId === user.id}
                              className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                              title="Delete admin user"
                            >
                              {deletingUserId === user.id ? (
                                <Loader className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="py-10 text-center text-slate-500">
                        No admin users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Admin User</DialogTitle>
            <DialogDescription>
              {editingUser
                ? "Update this admin account. Leave password blank to keep the current password."
                : "Add a new admin account using the existing registration flow."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  className="pl-10"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number (Optional)</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  pattern="[6-9][0-9]{9}"
                  maxLength={10}
                  placeholder="9876543210"
                  className="pl-10"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                />
              </div>
              <p className="text-xs text-slate-500">
                Enter a 10-digit Indian mobile number.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  className="pl-10"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">
                Password {editingUser ? "(Optional)" : ""}
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={(e) => handleChange("password", e.target.value)}
                required={!editingUser}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating} className="gap-2">
                <UserPlus className="h-4 w-4" />
                {creating
                  ? editingUser
                    ? "Saving..."
                    : "Creating..."
                  : editingUser
                    ? "Save User"
                    : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Users;
